// @size-accept:file: Keep the additive ownership field in the existing host response contract; splitting unrelated host API operations is outside this fix.
import { api, ApiError } from '@/api'
import type { IngestionOffering } from './ingestion-capability.types'
import type { RuntimeWarning } from './runtimeWarnings'
export type { IngestionConnectorCapability, IngestionCapabilities, IngestionOfferingHostStatus, IngestionOfferingHost, IngestionOffering } from './ingestion-capability.types'

export interface AgentHostVPNConfig {
  id: string
  agentHostId: string
  clientPublicKey: string
  serverPublicKey: string
  assignedIp: string
  allowedIps: string[]
  status: 'pending' | 'active' | 'disconnected'
  lastConnectedAt?: string
  createdAt: string
  updatedAt: string

  // Gateway registration fields
  gatewayInternalEndpoint?: string
  gatewayStatus?: 'pending' | 'ready' | 'offline'
  gatewayRegisteredAt?: string
  gatewayLastHeartbeatAt?: string
  gatewayPeerCount?: number
  gatewayConnectedPeers?: number

  // Peer connection status (from gateway heartbeat)
  peerConnected: boolean
  peerLastHandshakeAt?: string
  peerTransferRx?: number
  peerTransferTx?: number
  peerRemoteEndpoint?: string
}

// Agent host flavour (framework type)
// Backend supports both for compatibility, UI only shows 'agion-sdk'
// Only agion-sdk is supported (langgraph is deprecated)
export type AgentHostFlavour = 'agion-sdk'

// SP3/SP6: GPU training host classification (ported from reboot/main).
export type AgentHostProfile = 'standard' | 'gpu_training'
export const GPU_TRAINING_HOST_PROFILE: AgentHostProfile = 'gpu_training'

// Agent assigned to a host via default_host_id (from agents table)
export interface AgentHostAssignment {
  agentId: string
  agentName: string
  displayName: string
  hostType: string
  hostId: string
  sourceName: string
  createdAt: string
}

// Mission linked to a host via specialist agents' default_host_id or direct host_id
export interface HostMissionAssignment {
  missionId: string
  missionName: string
  status: string
  missionType: string
  createdAt: string
}

// Runtime-registered agent (from registered_agents table)
export interface RegisteredAgent {
  id: string
  agentHostId: string
  agentId: string
  containerId: string
  manifestSha: string
  status: 'running' | 'stopped' | 'failed'
  lastReportedAt: string
  registeredAt: string
}

export interface AgentHost {
  infrastructureOwnership?: { managedEnvironment: { id: string; name: string } | null }
  id: string
  name: string
  /** Server-computed notices on the host detail, shown as given (agion/Agion#3123). */
  warnings?: RuntimeWarning[]
  /** Declared region projection; null means unknown, omission is a legacy response. */
  region?: string | null
  /** Current non-archived missions directly assigned to this host. */
  missionCount?: number | null
  description?: string
  hostType: 'vpn'
  flavour: AgentHostFlavour  // Agent framework flavour
  status: 'pending' | 'online' | 'offline' | 'degraded' | 'decommissioned'
  spiffeId: string
  lastHeartbeatAt?: string  // API returns last_heartbeat_at → lastHeartbeatAt
  agentCount: number
  bootstrapToken?: string
  createdAt: string
  updatedAt: string
  isPlatformDefault?: boolean  // Platform default host for AI operations

  // Certificate info (returned from GetHost with active certificate)
  activeCertificate?: {
    id: string
    serialNumber: string
    subjectDn: string // SPIFFE ID for SPIFFE certs
    notBefore: string
    notAfter: string
    status: 'active' | 'rotating' | 'revoked'
    revokedAt?: string
    revokedBy?: string
    revocationReason?: string
    createdAt: string
  }

  // VPN config (for VPN type hosts)
  vpnConfig?: AgentHostVPNConfig

  // Agents assigned to this host (via default_host_id)
  assignedAgents?: AgentHostAssignment[]

  // Missions linked to this host (through specialist agents or direct host_id)
  assignedMissions?: HostMissionAssignment[]

  // Runtime-registered agents (from registered_agents table)
  registeredAgents?: RegisteredAgent[]

  // Host group membership (cluster)
  hostGroupId?: string
  hostGroupName?: string

  /** The host's public chat address on the host detail (agion/Agion#3471); absent when it has none. */
  publicHostname?: string

  // Binary version fields (V159, V65, V240, V249) - reported via heartbeat
  agentHostVersion?: string
  agionMcpVersion?: string
  missionRunnerVersion?: string
  intelligenceVersion?: string
  // Reboot-line runtime package, side-by-side with the legacy zone (SP8)
  intelligenceRuntimeVersion?: string
  channelGatewayVersion?: string
  alloyVersion?: string
  lastVersionReportAt?: string

  // Edge settings revision the host last reported as applied (V379, PLAN-20261013
  // slice 09, A7). Absent when the host never reported; shown as given.
  appliedSettingsRevision?: number
  appliedSettingsEnvironmentId?: string
  appliedSettingsReportedAt?: string

  // Host-observed APT candidates. `isInstall` is present only when the host
  // has explicitly approved a policy-safe first install. An empty array means
  // a current host completed its APT check with no offers. Null/undefined mean
  // availability was unavailable or belongs to the legacy no-contract state.
  availableUpdates?: HostAvailablePackageUpdate[] | null

  // Persistent resource health (FortiClient VPN, etc.) — updated via heartbeat
  persistentResourceHealth?: PersistentResourceHealth[]

  // Additional metadata
  capacity?: {
    maxAgents: number
    currentAgents: number
  }
  metadata?: Record<string, unknown>
}

export interface PersistentResourceHealth {
  resourceId: string
  resourceType: string
  status: 'connected' | 'connecting' | 'disconnected' | 'degraded' | 'error'
  lastCheck: string
  message?: string
  metadata?: {
    // VPN-specific fields
    vpnType?: string
    server?: string
    port?: number
    namespace?: string
    proxySocket?: string
    proxyHealthy?: boolean
    connectedSince?: string
    uptimeSeconds?: number
    reconnectAttempts?: number
    // Tunnel-specific fields (Cloudflare Tunnel)
    connectorType?: string    // "cloudflared"
    tunnelId?: string
    accountId?: string
    hostname?: string
    originPort?: number
    clusterMode?: string      // "active_active"
    isLeader?: boolean
    startedSince?: string
  }
}

export interface AgentHostListResponse {
  hosts: AgentHost[]
  total: number
}

export interface CreateAgentHostRequest {
  name: string
  description?: string
  hostType: 'vpn'
  flavour: AgentHostFlavour  // Agent framework flavour
  metadata?: Record<string, unknown>  // Cloud provider and other config
}

export interface CreateAgentHostResponse {
  host: AgentHost
  bootstrapToken: string
  bootstrapConfig?: string  // Base64-encoded JSON with all bootstrap info (all host types)
}

export interface RegenerateTokenResponse {
  token: string
  expiresAt: string
  bootstrapConfig?: string  // Base64-encoded JSON with all bootstrap info (all host types)
}

// API response after automatic snake_case → camelCase conversion by apiClient
interface RegenerateTokenApiResponse {
  bootstrapToken: string
  expiresAt: string
  bootstrapConfig?: string  // Base64-encoded JSON (all host types)
}

export interface AgentHostFilter {
  hostType?: 'vpn' | 'in-cluster'
  status?: 'pending' | 'online' | 'offline' | 'degraded' | 'decommissioned'
  q?: string
  hostGroupId?: string
  excludeDecommissioned?: boolean
  sortBy?: 'name' | 'status' | 'last_heartbeat_at' | 'created_at'
  sortOrder?: 'asc' | 'desc'
  limit?: number
  offset?: number
}

function metadataString(metadata: Record<string, unknown> | undefined, ...keys: string[]): string {
  if (!metadata) return ''
  for (const key of keys) {
    const value = metadata[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return ''
}

// GPU training-host helpers (ported from reboot/main; consumed by the SP3
// fine-tuning surface and later by the SP6 trainer flows).
export function isGpuTrainingHost(host: Pick<AgentHost, 'metadata'>): boolean {
  const metadata = host.metadata
  const profile = metadataString(metadata, 'hostProfile', 'host_profile')
  const workload = metadataString(metadata, 'workloadType', 'workload_type', 'workload')
  const runtimeRole = metadataString(metadata, 'runtimeRole', 'runtime_role')
  const accelerator = metadataString(metadata, 'acceleratorType', 'accelerator_type')
  return (
    profile === GPU_TRAINING_HOST_PROFILE ||
    workload === 'model_training' ||
    runtimeRole === 'gpu_trainer' ||
    accelerator === 'gpu'
  )
}

export function trainingHostInfraEnvironmentId(host: Pick<AgentHost, 'metadata'>): string {
  return metadataString(
    host.metadata,
    'infraEnvironmentId',
    'infra_environment_id',
    'cloudEnvironmentId',
    'cloud_environment_id',
    'environmentId',
    'environment_id'
  )
}

export function trainingHostGpuLabel(host: Pick<AgentHost, 'metadata'>): string {
  return metadataString(host.metadata, 'gpuType', 'gpu_type', 'gpuPlan', 'gpu_plan', 'plan') || 'GPU'
}

// Response from the effective-resources API
export interface EffectiveResourcesResponse {
  hostId: string
  bindings: Record<string, EffectiveBinding>
}

// Effective binding from the backend
export interface EffectiveBinding {
  resourceId: string
  resourceType: string
  /** Specific connector type (e.g. "shopify"). Lifted from
   *  resource_data.connector_type by the backend for UI convenience.
   *  Optional — legacy resources (pre-connector-registry) may not have it. */
  connectorType?: string
  resourceName: string
  alias: string
  priority: number
  effectiveQuota?: Record<string, unknown>
  /** Slice G.2 — Shopify Admin API version lifecycle. Set on Shopify
   *  resources only; empty/undefined for everything else. The capsule
   *  wizard renders ShopifyVersionStatusBanner from these. */
  apiVersionPinStrategy?: string
  apiVersionStatus?: string
  apiVersionStatusMessage?: string
}

// Transformed effective resource for frontend use
export interface EffectiveResource {
  id: string
  resourceId: string
  name: string
  resourceType: string
  /** Specific connector type (e.g. "shopify"). Mirrors EffectiveBinding.connectorType. */
  connectorType?: string
  alias?: string
  priority?: number
  effectiveQuota?: Record<string, unknown>
  /** Slice G.2 — Shopify api-version lifecycle (mirrors EffectiveBinding). */
  apiVersionPinStrategy?: string
  apiVersionStatus?: string
  apiVersionStatusMessage?: string
}


// Host API types (indexed OpenAPI specs)
export interface HostAPI {
  resourceId: string
  name: string
  description?: string
  baseUrl: string
  endpointCount: number
  schemaCount: number
  endpointsSummary?: string
}

export interface HostApisResponse {
  apis: HostAPI[]
  error?: string
  message?: string
}

// Model available on a host (from governance-service)
export interface HostModel {
  modelId: string
  modelName: string
  providerId: string
  providerName: string
  providerType: string
  capabilities?: string[]
  contextWindow?: number
  supportsVision?: boolean
}

export interface HostModelsResponse {
  hostId: string
  organizationId: string
  models: HostModel[]
  total: number
}

// ===== OTA Update Types =====

export interface ByohDashboard {
  totalHosts: number
  onlineHosts: number
  upToDate: number
  updatesAvailable: number
  criticalUpdates: number
  versionDistribution: Record<string, Record<string, number>>
  latestVersions: Record<string, string>
}

export interface AvailableUpdate {
  package: string
  current: string
  latest: string
  isSecurity: boolean
  isInstall?: boolean
  isRepair?: boolean
}

export interface HostAvailablePackageUpdate {
  name: string
  installed: string
  candidate: string
  isInstall?: boolean
  isRepair?: boolean
}

export interface HostUpdateEligibility {
  eligible: boolean
  critical: boolean
  availableUpdates: AvailableUpdate[]
}

export interface HostWithEligibility extends AgentHost {
  updateEligibility?: HostUpdateEligibility
  pendingUpdate?: HostUpdateRequest
}

export interface HostUpdateRequest {
  id: string
  organizationId: string
  hostId: string
  packages: PackageUpdate[]
  status: 'pending' | 'delivered' | 'running' | 'completed' | 'failed' | 'cancelled'
  errorMessage?: string
  createdBy: string
  createdAt: string
  deliveredAt?: string
  startedAt?: string
  completedAt?: string
}

export interface PackageUpdate {
  name: string
  version: string
}

export interface ByohPackageVersion {
  id: string
  packageName: string
  version: string
  channel: string
  isSecurityUpdate: boolean
  severity?: string
  releaseNotes?: string
  releasedAt: string
}

export interface FleetUpdateRequest {
  packages: PackageUpdate[]
  hostFilter: {
    status?: string
    maxCurrentVersion?: string
  }
}

export interface FleetUpdateResponse {
  updateCount: number
  hosts: string[]
}

export class AgentHostService {
  private readonly basePath = 'governance/agent-hosts'
  private readonly orchestrationPath = 'hosts' // orchestration-service path

  async listHosts(filter?: AgentHostFilter): Promise<AgentHostListResponse> {
    const params: Record<string, string> = {}

    if (filter?.hostType) {
      params['host_type'] = filter.hostType
    }
    if (filter?.status) {
      params['status'] = filter.status
    }
    if (filter?.q) params['q'] = filter.q
    if (filter?.hostGroupId) params['host_group_id'] = filter.hostGroupId
    if (filter?.excludeDecommissioned !== undefined) {
      params['exclude_decommissioned'] = String(filter.excludeDecommissioned)
    }
    if (filter?.sortBy) params['sort_by'] = filter.sortBy
    if (filter?.sortOrder) params['sort_order'] = filter.sortOrder
    if (filter?.limit !== undefined) {
      params['limit'] = filter.limit.toString()
    }
    if (filter?.offset !== undefined) {
      params['offset'] = filter.offset.toString()
    }

    const data = await api.get<AgentHostListResponse>(this.basePath, params)
    return data
  }

  async getHost(id: string): Promise<AgentHost> {
    return await api.get<AgentHost>(`${this.basePath}/${id}`)
  }

  async createHost(request: CreateAgentHostRequest): Promise<CreateAgentHostResponse> {
    return await api.post<CreateAgentHostResponse, CreateAgentHostRequest>(
      this.basePath,
      request
    )
  }

  async regenerateToken(id: string): Promise<RegenerateTokenResponse> {
    const response = await api.post<RegenerateTokenApiResponse, void>(
      `${this.basePath}/${id}/regenerate-token`,
      undefined
    )
    // apiClient already converts snake_case → camelCase
    // Map bootstrapToken to token for consistency with component expectations
    return {
      token: response.bootstrapToken,
      expiresAt: response.expiresAt,
      bootstrapConfig: response.bootstrapConfig,
    }
  }

  async decommissionHost(id: string): Promise<void> {
    await api.delete<void>(`${this.basePath}/${id}`)
  }

  async updateHost(id: string, updates: Partial<AgentHost>): Promise<AgentHost> {
    return await api.put<AgentHost, Partial<AgentHost>>(
      `${this.basePath}/${id}`,
      updates
    )
  }

  /**
   * Get effective resources for a host (resources bound to this host)
   * Used by Agent Designer to show only resources available to a BYOH host
   */
  async getEffectiveResources(hostId: string): Promise<EffectiveResource[]> {
    const data = await api.get<EffectiveResourcesResponse>(
      `${this.basePath}/${hostId}/effective-resources`
    )

    // Transform map of bindings to array
    if (!data.bindings) {
      return []
    }

    return Object.entries(data.bindings).map(([key, binding]) => ({
      id: key,
      resourceId: binding.resourceId,
      name: binding.resourceName,
      resourceType: binding.resourceType,
      connectorType: binding.connectorType,
      apiVersionPinStrategy: binding.apiVersionPinStrategy,
      apiVersionStatus: binding.apiVersionStatus,
      apiVersionStatusMessage: binding.apiVersionStatusMessage,
      alias: binding.alias,
      priority: binding.priority,
      effectiveQuota: binding.effectiveQuota,
    }))
  }

  /**
   * The organisation's ingestion offering, the single source of connector
   * capability: governance-service dials the named host's worker (or every
   * online host when no host is named) and reports each declaration with the
   * organisation's exclusions applied. A host that does not answer is
   * reported with a status; there is no fallback list on purpose.
   */
  async getIngestionOffering(hostId?: string): Promise<IngestionOffering> {
    // retry: 0. The client would otherwise replay every failing GET four
    // times, and each failure class is stable for the host.
    return await api.get<IngestionOffering>(
      'governance/ingestion-offering',
      hostId ? { host_id: hostId } : undefined,
      { retry: 0 },
    )
  }

  /**
   * Get indexed OpenAPI specs for a host (via streaming-service query)
   * Used by Mission Designer to show available APIs for workflow generation
   */
  async getHostApis(hostId: string): Promise<HostApisResponse> {
    return await api.get<HostApisResponse>(
      `${this.orchestrationPath}/${hostId}/apis`
    )
  }

  /**
   * Get detailed structure of a specific API on a host
   * Returns the full endpoint and schema information
   */
  async getHostApiStructure(hostId: string, resourceId: string): Promise<unknown> {
    return await api.get<unknown>(
      `${this.orchestrationPath}/${hostId}/apis/${resourceId}`
    )
  }

  /**
   * Get models available on a host
   * Returns all models that have credentials provisioned for the host's organization
   * Used by Tool Designer to filter model dropdown to only show available models
   */
  async getHostModels(hostId: string): Promise<HostModelsResponse> {
    return await api.get<HostModelsResponse>(`${this.basePath}/${hostId}/models`)
  }

  // ===== BYOH OTA Update Methods =====

  async getDashboard(): Promise<ByohDashboard> {
    return await api.get<ByohDashboard>(`${this.basePath}/dashboard`)
  }

  async listHostsWithEligibility(params?: Record<string, string>): Promise<{
    hosts: HostWithEligibility[]
    total: number
  }> {
    return await api.get<{ hosts: HostWithEligibility[]; total: number }>(
      `${this.basePath}/eligible`,
      params
    )
  }

  async listPackageVersions(params?: Record<string, string>): Promise<{
    versions: ByohPackageVersion[]
    count: number
  }> {
    return await api.get<{ versions: ByohPackageVersion[]; count: number }>(
      `${this.basePath}/versions`,
      params
    )
  }

  async createHostUpdate(hostId: string, packages: PackageUpdate[]): Promise<HostUpdateRequest> {
    return await api.post<HostUpdateRequest>(
      `${this.basePath}/${hostId}/updates`,
      { packages }
    )
  }

  async listUpdates(params?: Record<string, string>): Promise<{
    updates: HostUpdateRequest[]
    total: number
  }> {
    return await api.get<{ updates: HostUpdateRequest[]; total: number }>(
      `${this.basePath}/updates`,
      params
    )
  }

  async getUpdate(requestId: string): Promise<HostUpdateRequest> {
    return await api.get<HostUpdateRequest>(`${this.basePath}/updates/${requestId}`)
  }

  async cancelUpdate(requestId: string): Promise<void> {
    await api.delete<void>(`${this.basePath}/updates/${requestId}`)
  }

  async createFleetUpdate(req: FleetUpdateRequest): Promise<FleetUpdateResponse> {
    return await api.post<FleetUpdateResponse>(`${this.basePath}/fleet-updates`, req)
  }

  async checkForUpdates(hostId: string): Promise<{ status: string }> {
    return await api.post<{ status: string }>(`${this.basePath}/${hostId}/check-updates`, {})
  }

  // ===== Platform Default Host Methods =====

  /**
   * Get the current platform default host, or null when the organisation has
   * none. Governance answers 200 with a JSON null for that state
   * (agion/Agion#3062); older images answer 404, read the same way. Every other
   * failure rethrows so the page can tell failure from absence.
   */
  async getDefaultHost(): Promise<AgentHost | null> {
    try {
      return (await api.get<AgentHost | null>(`${this.basePath}/default`)) ?? null
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) return null
      throw error
    }
  }

  /**
   * Set a host as the platform default
   * All AI operations (search, assistant, model connections) will route through this host
   */
  async setDefaultHost(hostId: string): Promise<void> {
    await api.put<void>(`${this.basePath}/${hostId}/default`, undefined)
  }

  /**
   * Get the platform-intelligence capsule bootstrap status for a host.
   * `ready=true` means the __platform capsule exists on the host (the
   * normal "Platform Default" indicator). `ready=false` means a reindex
   * is in flight or has not finished yet ("Bootstrapping" indicator).
   * Throws when the host is unreachable — callers treat that as unknown.
   *
   * Backend returns `{ready, checked_at}`; the api-client adapter
   * converts to camelCase here.
   */
  async getPlatformCapsuleStatus(hostId: string): Promise<{ ready: boolean; checkedAt: string }> {
    return await api.get<{ ready: boolean; checkedAt: string }>(
      `${this.basePath}/${hostId}/platform-capsule-status`,
    )
  }
}

export const agentHostService = new AgentHostService()
